import React from "react";
import { useNavigate } from "react-router-dom";
import "./PrivacyPolicy.css";

function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="PrivacyPolicy">
      <div className="privacyHeader">
        <img
          src="/Assets/Header/amalgamateLogo.svg"
          alt="AmalgaMate Logo"
          className="privacyLogo"
          onClick={() => navigate("/amalgamate")}
        />
        <span className="privacyHeaderText">PRIVACY POLICY</span>
      </div>

      <div className="privacyPolicyContainer">
        <div className="privacyPolicyContainer2">
          <span className="lastUpdatedText">Effective Date: October 2026 | Last Updated: October 2026</span>

          <div className="highlightBox">
            <p>
              <strong>Our Commitment:</strong> AmalgaMate Unification Services LTD. ("AmalgaMate", "we", "us", or "our") is dedicated to empowering humanity through democratic consensus, knowledge certification, trusted communities, and decentralized marketplace exchange while maintaining the highest standards of user privacy, authentic verification, and transparent data stewardship.
            </p>
          </div>

          {/* Section 1 */}
          <div className="legalSection">
            <span className="smallHeaderText">1. Introduction & Scope</span>
            <p className="smallText">
              This Privacy Policy applies to all users of the AmalgaMate web applications, mobile platforms, APIs, democratic voting tools, video shorts network, learning and certification modules, community hubs, marketplace offerings, custom quote systems, wallet dashboard, and related services (collectively, the "Platform").
            </p>
            <p className="smallText">
              By accessing, registering with, or interacting with our Platform, you acknowledge that you have read and understood the collection, processing, and management of your information as described in this Policy. If you do not agree with any part of this Policy, please refrain from using the Platform.
            </p>
          </div>

          {/* Section 2 */}
          <div className="legalSection">
            <span className="smallHeaderText">2. Information We Collect</span>
            <p className="smallText">
              We collect information strictly necessary to provide authentic, secure, and personalized experiences across our platform features:
            </p>

            <h4 className="subSectionHeader">2.1 Account & Identity Information</h4>
            <ul className="legalList">
              <li><strong>Personal Profile Data:</strong> Full name, verified email address, mobile phone number, age bracket, and profile picture/avatar.</li>
              <li><strong>Authentication Credentials:</strong> Passwords, multi-factor authentication tokens, and SMS OTP verification logs.</li>
              <li><strong>Profile Questionnaires:</strong> Self-identified professional backgrounds, areas of expertise, interests, and profile details provided during onboarding.</li>
            </ul>

            <h4 className="subSectionHeader">2.2 Geographic Location & Address Information</h4>
            <ul className="legalList">
              <li><strong>Location Data:</strong> Country, state/province, city, and residential address details provided during registration or profile updates.</li>
              <li><strong>Purpose Limitation:</strong> Used to deliver location-relevant ballots, community localizations, marketplace order fulfillment, and required invoicing/tax compliance for commercial marketplace earnings.</li>
            </ul>

            <h4 className="subSectionHeader">2.3 Facial Recognition & Identity Verification Data</h4>
            <ul className="legalList">
              <li><strong>Live Face Recognition:</strong> When you register your face, sign in with face recognition, or re-authenticate for sensitive actions, camera snapshot frames are captured to verify human presence and match against your registered profile.</li>
              <li><strong>Verification Status:</strong> Verification status indicators (unverified, pending, verified, or rejected) associated with government identity documents submitted to ensure verified human status.</li>
              <li><strong>Privacy Guarantee:</strong> Facial data is utilized strictly for identity authentication, anti-bot protection, and one-person-one-vote ballot integrity. We never sell, rent, or trade your facial verification data.</li>
            </ul>

            <h4 className="subSectionHeader">2.4 Democratic Ballots, Polls & Civic Participation</h4>
            <ul className="legalList">
              <li><strong>Voting Records:</strong> Ballots created, option votes cast, sub-ballot selections, saved ballot bookmarks, and voting history.</li>
              <li><strong>Public vs. Aggregated Data:</strong> Total vote counts, option percentages, and aggregate distributions are publicly visible. Your individual ballot selections remain private to your account. Public comments, likes, and dislikes on ballots are visible to the community.</li>
            </ul>

            <h4 className="subSectionHeader">2.5 Media, Shorts Videos & User-Generated Content</h4>
            <ul className="legalList">
              <li><strong>Video Uploads:</strong> Video files, audio tracks, titles, descriptions, hashtags, captions, and thumbnail media uploaded to the Shorts Video feed.</li>
              <li><strong>Social Engagement:</strong> Likes, video comments, shares, views, watch history, and content moderation reports.</li>
            </ul>

            <h4 className="subSectionHeader">2.6 Certifications, Learning Modules & Exam Integrity</h4>
            <ul className="legalList">
              <li><strong>Learning Progress:</strong> Modules viewed, courses completed, syllabus milestones, and learning time.</li>
              <li><strong>Examination Data:</strong> Exam responses, multiple-choice submissions, test scores, timestamps, and pass/fail metrics.</li>
              <li><strong>Verifiable Digital Certificates:</strong> Public certificate identifiers, verification keys, issue dates, and skill accreditations linked to your profile.</li>
            </ul>

            <h4 className="subSectionHeader">2.7 Communities & Social Engagement</h4>
            <ul className="legalList">
              <li><strong>Community Activity:</strong> Communities created, memberships joined, group discussion threads, messages, and administrative actions.</li>
              <li><strong>Safety & Moderation:</strong> Moderation logs, community rule violation flags, and user report histories.</li>
            </ul>

            <h4 className="subSectionHeader">2.8 Marketplace, Offerings, Custom Quotes & Fulfillment</h4>
            <ul className="legalList">
              <li><strong>Provider Offerings:</strong> Service packages, activities, experiences, descriptions, pricing, schedules, participant limits, and availability.</li>
              <li><strong>Custom Quotes:</strong> Custom project briefs, quote requests, provider proposals, itemized pricing, and agreed scope parameters.</li>
              <li><strong>Orders & Fulfillment:</strong> Booking transactions, fulfillment progress, tracking numbers, proof-of-fulfillment uploads, customer reviews, and star ratings.</li>
            </ul>

            <h4 className="subSectionHeader">2.9 Payments, Earnings & Payout Information</h4>
            <ul className="legalList">
              <li><strong>Payment Transactions:</strong> Payment method tokens, transaction amounts, timestamps, and invoices processed via PCI-DSS Level 1 compliant processors (Stripe). AmalgaMate does not store raw payment card numbers.</li>
              <li><strong>Earnings & Escrow Tracking:</strong> Marketplace provider revenue, escrow transaction states, and affiliate referral commissions visible on the wallet dashboard.</li>
              <li><strong>Provider Payout Information:</strong> Bank account routing details and Stripe Connect onboarding information required to disburse earned funds and comply with financial regulations.</li>
            </ul>

            <h4 className="subSectionHeader">2.10 Affiliate, Referrals & Rewards</h4>
            <ul className="legalList">
              <li><strong>Affiliate Tracking:</strong> Unique referral codes, referral link clicks, registered referrals, and commission balances.</li>
              <li><strong>Rewards:</strong> Platform activity points accrued and redemption history.</li>
            </ul>

            <h4 className="subSectionHeader">2.11 Technical, Device & Analytics Information</h4>
            <ul className="legalList">
              <li><strong>Device & Network Data:</strong> IP addresses, browser types, operating systems, and device characteristics.</li>
              <li><strong>Security Logs:</strong> Login attempts, session timestamps, and automated anti-bot security records.</li>
            </ul>
          </div>

          {/* Section 3 */}
          <div className="legalSection">
            <span className="smallHeaderText">3. How We Use Your Information</span>
            <p className="smallText">We process your personal data for clear, legitimate operational purposes:</p>
            <ul className="legalList">
              <li><strong>Authentication & Security:</strong> To authenticate your identity via credentials, OTP, and face verification, preventing unauthorized access, duplicate accounts, and automated bots.</li>
              <li><strong>Democratic Ballot Administration:</strong> To accurately tabulate ballot results, display real-time percentage distributions, and maintain voting integrity.</li>
              <li><strong>Educational Administration:</strong> To evaluate certification exams, issue verifiable digital certificates, and maintain credential verification registers.</li>
              <li><strong>Marketplace Facilitation:</strong> To connect buyers with verified service providers, process custom quote contracts, manage order fulfillment, and disburse provider earnings.</li>
              <li><strong>Video & Community Distribution:</strong> To host, transcode, stream user video shorts, facilitate discussions, and enforce community safety rules.</li>
              <li><strong>Financial Accounting & Compliance:</strong> To track order escrow states, process voluntary donations, disburse earnings via Stripe Connect, and comply with commercial and tax obligations.</li>
              <li><strong>Platform Improvement:</strong> To troubleshoot technical errors, optimize UI performance, and power AI assistants for ballot drafting and learning workflows.</li>
              <li><strong>Communications:</strong> To send critical transaction receipts, security notices, quote updates, and platform policy announcements.</li>
            </ul>
          </div>

          {/* Section 4 */}
          <div className="legalSection">
            <span className="smallHeaderText">4. Biometric Data Privacy & Governance</span>
            <div className="highlightBox">
              <p>
                <strong>Strict Biometric Guarantee:</strong> We treat facial verification data with utmost care. AmalgaMate NEVER sells, leases, trades, or commercially exploits facial recognition data to any third party for marketing or advertising purposes.
              </p>
            </div>
            <ul className="legalList">
              <li><strong>Explicit Consent:</strong> Live face recognition is activated only with your explicit permission when registering or logging in.</li>
              <li><strong>Purpose Limitation:</strong> Facial data is utilized strictly for identity authentication, bot prevention, and ensuring one vote per individual.</li>
              <li><strong>Destruction Protocol:</strong> Facial verification records are deleted upon verified account termination or upon request to support.</li>
            </ul>
          </div>

          {/* Section 5 */}
          <div className="legalSection">
            <span className="smallHeaderText">5. Data Sharing & Third-Party Disclosures</span>
            <p className="smallText">We do not sell your personal data. We disclose information only under specific operational conditions:</p>
            <ul className="legalList">
              <li><strong>Cloud Infrastructure Subprocessors:</strong> Secure cloud hosting, database providers, video streaming pipelines, and SMS/email delivery providers bound by strict confidentiality and data protection agreements.</li>
              <li><strong>Payment & Payout Processors:</strong> Certified payment gateways (such as Stripe) to process payments, donations, and provider bank payouts.</li>
              <li><strong>Marketplace Counterparties:</strong> Necessary booking, delivery, or custom project details shared directly between Buyer and Provider to execute confirmed transactions.</li>
              <li><strong>Public Verification Registers:</strong> Publicly verifiable digital certificates (displaying candidate name, certificate ID, issue date, and credential status) when shared by the certificate holder.</li>
              <li><strong>Legal & Regulatory Compliance:</strong> Competent judicial or regulatory authorities when strictly required by applicable law, court order, or subpoena.</li>
            </ul>
          </div>

          {/* Section 6 */}
          <div className="legalSection">
            <span className="smallHeaderText">6. Data Visibility & User Privacy Controls</span>
            <ul className="legalList">
              <li><strong>Public by Default:</strong> Display name, profile avatar, public community posts, published marketplace offerings, and uploaded Shorts videos.</li>
              <li><strong>Private by Default:</strong> Email address, mobile phone number, exact residential address, individual voting history, earnings ledger, and facial credentials.</li>
              <li><strong>User Settings:</strong> You can manage profile details, notification preferences, and account security options from your account dashboard.</li>
            </ul>
          </div>

          {/* Section 7 */}
          <div className="legalSection">
            <span className="smallHeaderText">7. Cookies & Local Storage</span>
            <p className="smallText">
              We employ essential session tokens, authentication cookies, and performance analytics to keep you signed in, remember your preferences, and maintain responsive performance. You can control cookie preferences in your browser settings, though disabling essential cookies will prevent logging in and participating on the platform.
            </p>
          </div>

          {/* Section 8 */}
          <div className="legalSection">
            <span className="smallHeaderText">8. Data Security & Retention</span>
            <p className="smallText">We maintain multi-layer administrative and technical safeguards:</p>
            <ul className="legalList">
              <li>Encrypted data transport using modern TLS/HTTPS protocols.</li>
              <li>Access controls, hashed authentication credentials, and automated anti-bot protections.</li>
              <li><strong>Retention:</strong> Active account information is retained for the lifetime of your account. Transaction records are retained as required by financial, tax, and accounting regulations.</li>
              <li><strong>Account Deletion:</strong> You may request account deletion at any time by contacting support. Upon account deletion, personal profile and facial recognition data are removed from active production systems.</li>
            </ul>
          </div>

          {/* Section 9 */}
          <div className="legalSection">
            <span className="smallHeaderText">9. Your Global Privacy Rights</span>
            <p className="smallText">
              In accordance with applicable privacy frameworks (including GDPR, PIPEDA, CCPA/CPRA, and relevant data protection laws), you hold the following rights:
            </p>
            <ul className="legalList">
              <li><strong>Right of Access:</strong> Request a copy of the personal information we hold regarding your account.</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete personal details.</li>
              <li><strong>Right to Erasure:</strong> Request the deletion of your account and personal data.</li>
              <li><strong>Right to Restriction & Objection:</strong> Object to or request restriction of specific processing activities.</li>
              <li><strong>Right to Data Portability:</strong> Obtain your personal account data in a structured, machine-readable format.</li>
              <li><strong>Non-Discrimination:</strong> We will never discriminate against you for exercising your legitimate privacy rights.</li>
            </ul>
          </div>

          {/* Section 10 */}
          <div className="legalSection">
            <span className="smallHeaderText">10. Children's Privacy</span>
            <p className="smallText">
              Our Platform is intended for individuals aged 16 and older. Commercial marketplace listings, provider payouts, and financial transactions require users to be at least 18 years of age. We do not knowingly collect personal information from individuals under 16. If we become aware that a child under 16 has submitted personal information, we will promptly delete it.
            </p>
          </div>

          {/* Section 11 */}
          <div className="legalSection">
            <span className="smallHeaderText">11. International Transfers</span>
            <p className="smallText">
              AmalgaMate operates globally with corporate headquarters in Canada. If you access the Platform from outside Canada, your information may be processed and stored in systems located in jurisdictions with different privacy laws, subject to standard contractual safeguards ensuring equivalent data protection.
            </p>
          </div>

          {/* Section 12 */}
          <div className="legalSection">
            <span className="smallHeaderText">12. Policy Updates</span>
            <p className="smallText">
              We may update this Privacy Policy periodically to reflect new platform features, technical adjustments, or legal requirements. When significant revisions are made, we will notify users through platform notices or email. Continued use of the Platform after the effective date signifies acceptance of the revised Policy.
            </p>
          </div>

          {/* Section 13 */}
          <div className="legalSection">
            <span className="smallHeaderText">13. Contact Information</span>
            <p className="smallText">
              For questions, privacy inquiries, or data subject requests, please contact our team:
            </p>
            <ul className="legalList">
              <li><strong>Corporate Entity:</strong> AmalgaMate Unification Services LTD.</li>
              <li><strong>Email:</strong> <a href="mailto:support@amalgamate.ca" className="emailText">support@amalgamate.ca</a></li>
              <li><strong>Website:</strong> <span className="emailText" onClick={() => navigate("/amalgamate")} style={{ cursor: "pointer" }}>amalgamate.ca</span></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
